import { MOBILE_PHONE_ERROR_KEYS, type UserDetailsResponse } from '~/services/modules/users/types';
import { userHasNoPhone } from '~/utils/phoneValidation';
import { genderFromValidateCode } from './genderFromValidateCode';

export type AfterCodeStep = 'phone_after_code' | 'gender_after_code' | 'equipment';

const HTTP_UNAUTHORIZED = 401;
const HTTP_CONFLICT = 409;
export const DEFAULT_SAVE_PHONE_ERROR = 'Não foi possível salvar o seu celular. Tente novamente.';
export const SESSION_EXPIRED_ERROR = 'Sua sessão expirou. Valide o código de verificação novamente.';

/** Depois do celular (ou quando ele já existe): sem gênero pergunta o gênero, senão vai para o equipamento. */
export function nextStepAfterPhone(rawGender: string | null | undefined): AfterCodeStep {
  return genderFromValidateCode(rawGender) ? 'equipment' : 'gender_after_code';
}

/**
 * Código validado. Mesma regra do chat legado (CORE-1025, `handleAfterCodeValidated`):
 * cadastro sem telefone pede o celular; depois, o gênero quando falta; por fim o equipamento.
 */
export function nextStepAfterCode(
  user: Partial<UserDetailsResponse> | null | undefined,
  rawGender: string | null | undefined
): AfterCodeStep {
  if (userHasNoPhone(user)) return 'phone_after_code';
  return nextStepAfterPhone(rawGender);
}

type ApiErrorShape = {
  response?: { status?: number; data?: { notifications?: Array<{ key?: string; message?: string }> } };
};

export type PhoneSaveOutcome =
  | { kind: 'continue' }
  | { kind: 'session_expired'; message: string }
  | { kind: 'error'; message: string };

/** Erros do `PUT /v2/users/me/mobile-phone`, tratados como no chat legado. */
export function classifyPhoneSaveError(error: unknown): PhoneSaveOutcome {
  const response = (error as ApiErrorShape)?.response;
  const notification = response?.data?.notifications?.[0];
  // Já tem celular (ex.: gravado noutra aba): nada a gravar, segue.
  if (notification?.key === MOBILE_PHONE_ERROR_KEYS.ALREADY_REGISTERED) return { kind: 'continue' };
  if (response?.status === HTTP_UNAUTHORIZED) return { kind: 'session_expired', message: SESSION_EXPIRED_ERROR };
  return { kind: 'error', message: notification?.message || DEFAULT_SAVE_PHONE_ERROR };
}

/**
 * `POST /v2/auth/validate-code?organization_id=` devolve 409 quando o CPF é de outra organização.
 * Nesse caso o `person_id` não pode ir como `customer_id` na cotação (hotfix conflito-person-org-cotacao).
 */
export function validateCodeConflict(error: unknown): boolean {
  return (error as ApiErrorShape)?.response?.status === HTTP_CONFLICT;
}
